import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, type ServerView, type ServerEvent, type Backup } from "../lib/api";
import { StatusPill } from "../components/StatusPill";
import { bytes, date, money } from "../lib/format";

const TRANSIENT = ["payment_confirmed", "provisioning", "installing", "starting"];

export function ServerDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [server, setServer] = useState<ServerView | null>(null);
  const [events, setEvents] = useState<ServerEvent[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { server } = await api.get<{ server: ServerView }>(`/servers/${id}`);
      setServer(server);
      const [{ events }, { backups }] = await Promise.all([
        api.get<{ events: ServerEvent[] }>(`/servers/${id}/events`),
        api.get<{ backups: Backup[] }>(`/servers/${id}/backups`),
      ]);
      setEvents(events);
      setBackups(backups);
      if (["ready", "running", "stopped"].includes(server.status)) {
        const { logs } = await api.get<{ logs: string[] }>(`/servers/${id}/logs`);
        setLogs(logs);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while the server is provisioning so the UI advances automatically.
  useEffect(() => {
    if (server && TRANSIENT.includes(server.status)) {
      const t = setInterval(load, 1500);
      return () => clearInterval(t);
    }
  }, [server?.status, load]);

  async function action(fn: () => Promise<unknown>, key: string) {
    setBusy(key);
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy("");
    }
  }

  if (error && !server) return <div className="page"><div className="error-box">{error}</div></div>;
  if (!server) return <div className="spinner">Loading…</div>;

  const isProvisioning = TRANSIENT.includes(server.status);
  const isReady = ["ready", "running", "stopped"].includes(server.status);

  return (
    <div className="page">
      <div className="row between">
        <div>
          <h1 data-testid="server-name">{server.name}</h1>
          <p className="muted">{server.game.name} · {server.plan.name} · {server.region.name}</p>
        </div>
        <StatusPill status={server.status} />
      </div>

      {params.get("checkout") === "success" && (
        <div className="notice mt" data-testid="checkout-success">Payment received — your server is being provisioned.</div>
      )}
      {error && <div className="error-box mt">{error}</div>}
      {isProvisioning && (
        <div className="notice mt" data-testid="provisioning-notice">Provisioning in progress… this page updates automatically.</div>
      )}
      {server.status === "failed" && (
        <div className="error-box mt" data-testid="failed-notice">
          Provisioning failed{server.lastError ? `: ${server.lastError}` : ""}. We retry automatically; contact support if this persists.
        </div>
      )}

      <div className="grid grid-2 mt">
        <div className="card">
          <h3>Connection</h3>
          {server.connectionAddress ? (
            <div className="row mt">
              <span className="addr" data-testid="connection-address">{server.connectionAddress}</span>
            </div>
          ) : (
            <p className="muted">Available once your server is ready.</p>
          )}
          <dl className="kv mt">
            <dt>Status</dt><dd><StatusPill status={server.status} /></dd>
            <dt>Plan</dt><dd>{server.plan.name} · {money(server.plan.priceCents)}/mo</dd>
            <dt>RAM / Disk</dt><dd>{server.plan.ramMb / 1024} GB / {server.plan.diskGb} GB</dd>
            <dt>Region</dt><dd>{server.region.name}</dd>
            <dt>Created</dt><dd>{date(server.createdAt)}</dd>
          </dl>
        </div>

        <div className="card">
          <h3>Controls</h3>
          <div className="row mt">
            <button className="btn btn-primary btn-sm" disabled={!isReady || !!busy} data-testid="power-start" onClick={() => action(() => api.post(`/servers/${id}/power`, { action: "start" }), "start")}>Start</button>
            <button className="btn btn-sm" disabled={!isReady || !!busy} data-testid="power-restart" onClick={() => action(() => api.post(`/servers/${id}/power`, { action: "restart" }), "restart")}>Restart</button>
            <button className="btn btn-sm" disabled={!isReady || !!busy} data-testid="power-stop" onClick={() => action(() => api.post(`/servers/${id}/power`, { action: "stop" }), "stop")}>Stop</button>
          </div>
          <div className="row mt">
            <button className="btn btn-ghost btn-sm" disabled={!isReady || !!busy} data-testid="create-backup" onClick={() => action(() => api.post(`/servers/${id}/backups`), "backup")}>Create backup</button>
            {server.subscriptionId && (
              <button className="btn btn-danger btn-sm" disabled={!!busy} data-testid="cancel-sub" onClick={() => action(() => api.post(`/billing/subscriptions/${server.subscriptionId}/cancel`), "cancel")}>Cancel subscription</button>
            )}
          </div>
        </div>
      </div>

      <section className="section">
        <h2>Backups</h2>
        {backups.length === 0 ? (
          <p className="muted">No backups yet.</p>
        ) : (
          <table className="table" data-testid="backups-table">
            <thead><tr><th>Created</th><th>Reason</th><th>Size</th><th>Status</th><th>Expires</th><th></th></tr></thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.id}>
                  <td>{date(b.createdAt)}</td>
                  <td>{b.reason}</td>
                  <td>{bytes(b.sizeBytes)}</td>
                  <td>{b.status}</td>
                  <td>{date(b.expiresAt)}</td>
                  <td>{b.status === "complete" && <button className="btn btn-danger btn-sm" disabled={!!busy} onClick={() => action(() => api.del(`/servers/${id}/backups/${b.id}`), "del")}>Delete</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="section">
        <h2>Console</h2>
        <div className="console" data-testid="console">{logs.length ? logs.join("\n") : "No console output yet."}</div>
      </section>

      <section className="section">
        <h2>Activity</h2>
        <table className="table" data-testid="events-table">
          <thead><tr><th>When</th><th>Type</th><th>Message</th></tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}><td>{date(e.createdAt)}</td><td><span className="tag">{e.type}</span></td><td>{e.message}</td></tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
